// NativeSRSView — Phase 1 native SRS placeholder for #/hisni.
// Shows stats + sync button + feature flag toggle. Full review UI in Phase 2.
import type { AweniContext } from '../../types';
import { computeSRSStats, seedAyahCards } from './srs/IndexedDBBridge';

export async function mountNativeSRSView(
  container: HTMLElement,
  ctx: AweniContext,
): Promise<() => void> {
  const tr = (key: string, fallback: string) => ctx.i18n.t(key) || fallback;
  container.innerHTML = `
    <div class="hisni-native" style="padding:var(--ln-space-4);max-width:480px;margin:0 auto">
      <div class="hisni-stats" style="
        background:var(--ln-bg-card);
        border-radius:var(--ln-radius-lg);
        padding:var(--ln-space-4);
        margin-bottom:var(--ln-space-4);
        box-shadow:var(--ln-shadow-sm)">
        <p style="color:var(--ln-text-secondary);font-size:.8rem;margin:0 0 var(--ln-space-2)">
          ${tr('hisni_native_loading','Loading...')}
        </p>
      </div>
      <button id="hisni-sync-btn" style="
        width:100%;padding:var(--ln-space-3) var(--ln-space-4);
        background:var(--ln-accent);color:var(--ln-accent-text);
        border:none;border-radius:var(--ln-radius-md);
        font-size:1rem;cursor:pointer;margin-bottom:var(--ln-space-3)">
        ${tr('hisni_native_sync','Sync from server')}
      </button>
      <button id="hisni-disable-native" style="
        width:100%;padding:var(--ln-space-2) var(--ln-space-4);
        background:transparent;color:var(--ln-text-secondary);
        border:1px solid var(--ln-border);border-radius:var(--ln-radius-md);
        font-size:.9rem;cursor:pointer">
        ${tr('hisni_native_disable','Switch to classic view')}
      </button>
    </div>
  `;

  const statsEl = container.querySelector<HTMLElement>('.hisni-stats')!;

  async function renderStats() {
    try {
      const s = await computeSRSStats();
      const syncLine = s.lastSync
        ? `<br><span style="font-size:.75rem;color:var(--ln-text-secondary)">${tr('hisni_native_last_sync','Last sync')}: ${new Date(s.lastSync).toLocaleString()}</span>`
        : '';
      statsEl.innerHTML = `
        <div style="display:flex;gap:var(--ln-space-4);flex-wrap:wrap">
          ${[
            [tr('hisni_native_total','Total'), s.total],
            [tr('hisni_native_due','Due today'), s.dueToday],
            [tr('hisni_native_mastered','Mastered'), s.mastered],
          ].map(([label, val]) => `
            <div style="flex:1;min-width:80px;text-align:center">
              <div style="font-size:1.5rem;font-weight:700;color:var(--ln-accent)">${val}</div>
              <div style="font-size:.75rem;color:var(--ln-text-secondary)">${label}</div>
            </div>
          `).join('')}
        </div>${syncLine}
      `;
    } catch {
      statsEl.textContent = tr('hisni_native_stats_error','Could not load stats');
    }
  }

  await renderStats();

  const syncBtn = container.querySelector<HTMLButtonElement>('#hisni-sync-btn')!;
  const disableBtn = container.querySelector<HTMLButtonElement>('#hisni-disable-native')!;

  const token = (ctx as unknown as Record<string, unknown>).authToken as string | undefined;

  syncBtn.addEventListener('click', async () => {
    syncBtn.disabled = true;
    syncBtn.textContent = tr('hisni_native_syncing','Syncing...');
    try {
      const res = await fetch('/aweni/api/v1/gateway/memoriz/api/reviews/due', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cards = await res.json();
      await seedAyahCards(Array.isArray(cards) ? cards : (cards.cards ?? cards.data ?? []));
      await renderStats();
      syncBtn.textContent = tr('hisni_native_sync_ok','Synced!');
    } catch (e) {
      syncBtn.textContent = tr('hisni_native_sync_error','Sync failed');
    } finally {
      syncBtn.disabled = false;
      setTimeout(() => { syncBtn.textContent = tr('hisni_native_sync','Sync from server'); }, 3000);
    }
  });

  disableBtn.addEventListener('click', () => {
    localStorage.removeItem('aweni_hisni_native');
    window.location.reload();
  });

  return () => { container.innerHTML = ''; };
}
